import type { LotteryEditPhase } from '@lotta/feature-lottery-rules';
import { useUpdateLotteryMutation } from '@lotta/self-service/lottery-fe/data-access-state';
import { useCallback, useEffect } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { LotteryForm } from './LotteryForm';
import { LotteryFormContext } from './lottery-form-context';
import { fromPayload } from './modules/transform';
import type { ReviewErrorTranslationKeys } from './review-configuration/ReviewConfiguration';
import { allModulesTemplate } from './templates';
import type { LotteryConfigData } from './types';
import { buildApiError } from './utils/error';
import { withLoadingValues } from './utils/with-loading-values';
import { filterTemplate } from './utils/filter-template';

const reviewTranslationKeys: ReviewErrorTranslationKeys = {
  // eslint-disable-next-line i18next/no-literal-string
  titleTranslationKey: 'general-error',
  // eslint-disable-next-line i18next/no-literal-string
  bodyTranslationKey: 'edit-contest-inline-message-error',
};

export type EditLotteryFormProps = {
  retailUnit: string;
  lotteryId: string;
  editMode: LotteryEditPhase;
  onLotteryEdited: () => void;
  onCancelled: () => void;
};

export const EditLotteryForm: React.FC<EditLotteryFormProps> = withLoadingValues(
  ({ values, retailUnit, lotteryId, editMode, onLotteryEdited, onCancelled }) => {
    const templateId = values.lottery.lotteryTemplateId;
    const modules = values.lottery.modules;
    const template = filterTemplate(modules, allModulesTemplate);
    const data = fromPayload(values, modules);

    const [updateLottery, updateLotteryResult] = useUpdateLotteryMutation();
    const { isLoading: isUpdateLotteryLoading } = updateLotteryResult;

    const onFormSubmit: SubmitHandler<LotteryConfigData> = useCallback(
      (lotteryConfigData) => {
        updateLottery({ retailUnit, lotteryId, ...lotteryConfigData });
      },
      [updateLottery, retailUnit, lotteryId]
    );

    useEffect(() => {
      if (updateLotteryResult.isSuccess) {
        onLotteryEdited();
      }
    }, [updateLotteryResult, onLotteryEdited]);

    const resetAPIError = useCallback(() => {
      updateLotteryResult.reset();
    }, [updateLotteryResult]);

    return (
      <LotteryFormContext.Provider
        value={{ mode: editMode, template, result: { isUpdateLotteryLoading } }}
      >
        <LotteryForm
          values={data}
          templateId={templateId}
          template={template}
          retailUnit={retailUnit}
          onFormSubmit={onFormSubmit}
          onSecondaryAction={onCancelled}
          error={
            updateLotteryResult.isError
              ? buildApiError(updateLotteryResult.isError, reviewTranslationKeys, resetAPIError)
              : undefined
          }
        />
      </LotteryFormContext.Provider>
    );
  }
);
